/**
 * Health Check Page
 * 
 * Displays the connection status of all services used in the MiEROS platform.
 * Accessible at /health
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CSpinner,
  CBadge,
  CButton,
  CProgress,
  CTable,
  CTableHead,
  CTableBody,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CContainer,
  CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { 
  cilCheckCircle, 
  cilXCircle, 
  cilWarning, 
  cilReload,
  cilCloudDownload,
  cilStorage,
  cilLockLocked,
  cilWallet,
  cilHome,
  cilMediaPlay,
  cilGlobeAlt
} from '@coreui/icons';
import config from '../../../utils/EnvironmentSwitcher';
import { getAuth } from 'firebase/auth';

// Service configuration
const SERVICES = [
  {
    id: 'fhir',
    name: 'FHIR Server',
    description: 'Healthcare data storage (HL7 FHIR)',
    icon: cilStorage,
    checkUrl: `${config.fhirUrl}/fhir/metadata`,
    expectedStatus: 200,
    timeout: 10000,
    category: 'core',
    requiresAuth: true // FHIR server requires Firebase auth token
  },
  {
    id: 'api',
    name: 'MiEROS API',
    description: 'Main backend API server',
    icon: cilCloudDownload,
    checkUrl: `${config.apiUrl}/health`,
    fallbackUrl: `${config.apiUrl}`,
    expectedStatus: [200, 404], // 404 is ok if no health endpoint
    timeout: 10000,
    category: 'core'
  },
  {
    id: 'payments',
    name: 'Payment Service',
    description: 'Stripe payment processing',
    icon: cilWallet,
    checkUrl: `${config.paymentUrl}/health`,
    fallbackUrl: `${config.paymentUrl}`,
    expectedStatus: [200, 404],
    timeout: 10000,
    category: 'payments'
  },
  {
    id: 'ipfs',
    name: 'IPFS Gateway',
    description: 'Decentralized file storage',
    icon: cilGlobeAlt,
    checkUrl: 'https://ipfs.mieros.cloud/ipfs/QmUNLLsPACCz1vLxQVkXqqLX5R1X345qqfHbsf67hvA3Nn/', // empty-dir CID; the node's /api is private
    fallbackUrl: 'https://ipfs.mieros.cloud',
    expectedStatus: [200, 405], // 405 Method Not Allowed is acceptable
    timeout: 10000,
    category: 'blockchain'
  },
  {
    id: 'firebase',
    name: 'Firebase Auth',
    description: 'Authentication service',
    icon: cilLockLocked,
    checkType: 'firebase',
    timeout: 5000,
    category: 'auth'
  },
  {
    id: 'websocket',
    name: 'WebSocket Server',
    description: 'Real-time notifications',
    icon: cilMediaPlay,
    checkType: 'websocket',
    wsUrl: 'wss://mieapi.mieros.cloud/ws/',
    timeout: 5000,
    category: 'core'
  },
  {
    id: 'smartthings',
    name: 'SmartThings',
    description: 'Smart home integration',
    icon: cilHome,
    checkType: 'smartthings',
    timeout: 5000,
    category: 'integrations'
  }
];

// Status constants
const STATUS = {
  PENDING: 'pending',
  CHECKING: 'checking',
  HEALTHY: 'healthy',
  DEGRADED: 'degraded',
  UNHEALTHY: 'unhealthy',
  UNKNOWN: 'unknown'
};

// Get Firebase auth token for authenticated requests
const getAuthToken = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (user) {
      const token = await user.getIdToken();
      return token;
    }
  } catch (error) {
    console.warn('Failed to get auth token:', error);
  }
  return null;
};

// Check HTTP endpoint
const checkHttpEndpoint = async (service, authToken = null) => {
  const startTime = Date.now();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), service.timeout);

  // Build headers - include auth token if provided and service requires it
  const headers = {
    'Accept': 'application/json, application/fhir+json, */*'
  };
  
  // Add Authorization header for services that require authentication
  if (authToken && service.requiresAuth) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  try {
    const response = await fetch(service.checkUrl, {
      method: 'GET',
      signal: controller.signal,
      headers
    });

    clearTimeout(timeoutId);
    const latency = Date.now() - startTime;

    const expectedStatuses = Array.isArray(service.expectedStatus) 
      ? service.expectedStatus 
      : [service.expectedStatus];

    if (expectedStatuses.includes(response.status)) {
      return {
        status: STATUS.HEALTHY,
        latency,
        message: `Connected (${response.status})`,
        details: { statusCode: response.status, authenticated: !!authToken }
      };
    }

    // Handle 401 specifically for FHIR - may need authentication
    if (response.status === 401 && service.requiresAuth && !authToken) {
      return {
        status: STATUS.DEGRADED,
        latency,
        message: 'Authentication required - please log in',
        details: { statusCode: 401, needsAuth: true }
      };
    }

    // Try fallback URL if available
    if (service.fallbackUrl && response.status >= 400) {
      const fallbackResponse = await fetch(service.fallbackUrl, {
        method: 'HEAD',
        signal: controller.signal,
        headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {}
      });
      
      if (fallbackResponse.ok || fallbackResponse.status < 500) {
        return {
          status: STATUS.DEGRADED,
          latency: Date.now() - startTime,
          message: `Reachable but health endpoint unavailable`,
          details: { statusCode: fallbackResponse.status }
        };
      }
    }

    return {
      status: STATUS.UNHEALTHY,
      latency,
      message: `Unexpected status: ${response.status}`,
      details: { statusCode: response.status }
    };
  } catch (error) {
    clearTimeout(timeoutId);
    const latency = Date.now() - startTime;

    if (error.name === 'AbortError') {
      return {
        status: STATUS.UNHEALTHY,
        latency,
        message: 'Request timeout',
        details: { error: 'Timeout' }
      };
    }

    // Try to check if it's a CORS issue (service might be up but blocking)
    return {
      status: STATUS.UNKNOWN,
      latency,
      message: error.message || 'Connection failed',
      details: { error: error.message }
    };
  }
};

// Check Firebase connection
const checkFirebase = async () => {
  const startTime = Date.now();
  
  try {
    const auth = getAuth();
    // Check if Firebase is initialized
    if (auth && auth.app) {
      const latency = Date.now() - startTime;
      return {
        status: STATUS.HEALTHY,
        latency,
        message: auth.currentUser ? 'Connected (authenticated)' : 'Connected (not authenticated)',
        details: { 
          projectId: auth.app.options.projectId,
          authenticated: !!auth.currentUser
        }
      };
    }
    
    return {
      status: STATUS.UNHEALTHY,
      latency: Date.now() - startTime,
      message: 'Firebase not initialized',
      details: {}
    };
  } catch (error) {
    return {
      status: STATUS.UNHEALTHY,
      latency: Date.now() - startTime,
      message: error.message,
      details: { error: error.message }
    };
  }
};

// Check WebSocket connection
const checkWebSocket = async (wsUrl, timeout) => {
  const startTime = Date.now();
  
  return new Promise((resolve) => {
    try {
      const ws = new WebSocket(wsUrl);
      const timeoutId = setTimeout(() => {
        ws.close();
        resolve({
          status: STATUS.UNHEALTHY,
          latency: Date.now() - startTime,
          message: 'Connection timeout',
          details: { error: 'Timeout' }
        });
      }, timeout);

      ws.onopen = () => {
        clearTimeout(timeoutId);
        const latency = Date.now() - startTime;
        ws.close();
        resolve({
          status: STATUS.HEALTHY,
          latency,
          message: 'Connected',
          details: { url: wsUrl }
        });
      };

      ws.onerror = () => {
        clearTimeout(timeoutId);
        resolve({
          status: STATUS.UNKNOWN,
          latency: Date.now() - startTime,
          message: 'Connection failed (may be CORS)',
          details: { url: wsUrl }
        });
      };
    } catch (error) {
      resolve({
        status: STATUS.UNHEALTHY,
        latency: Date.now() - startTime,
        message: error.message,
        details: { error: error.message }
      });
    }
  });
};

// Check SmartThings integration
const checkSmartThings = async () => {
  const startTime = Date.now();
  const tokenData = localStorage.getItem('smartthings_token');
  
  if (!tokenData) {
    return {
      status: STATUS.UNKNOWN,
      latency: Date.now() - startTime,
      message: 'Not configured',
      details: { configured: false }
    };
  }

  try {
    const token = JSON.parse(tokenData);
    if (token.access_token) {
      return {
        status: STATUS.HEALTHY,
        latency: Date.now() - startTime,
        message: 'Connected',
        details: { configured: true, hasToken: true }
      };
    }
  } catch (_e) {
    // Token parse failed
  }

  return {
    status: STATUS.DEGRADED,
    latency: Date.now() - startTime,
    message: 'Token invalid or expired',
    details: { configured: true, hasToken: false }
  };
};

// Status badge component
const StatusBadge = ({ status }) => {
  const config = {
    [STATUS.PENDING]: { color: 'secondary', text: 'Pending' },
    [STATUS.CHECKING]: { color: 'info', text: 'Checking...' },
    [STATUS.HEALTHY]: { color: 'success', text: 'Healthy' },
    [STATUS.DEGRADED]: { color: 'warning', text: 'Degraded' },
    [STATUS.UNHEALTHY]: { color: 'danger', text: 'Unhealthy' },
    [STATUS.UNKNOWN]: { color: 'secondary', text: 'Unknown' }
  };

  const { color, text } = config[status] || config[STATUS.UNKNOWN];
  return <CBadge color={color}>{text}</CBadge>;
};

// Status icon component
const StatusIcon = ({ status }) => {
  const iconMap = {
    [STATUS.HEALTHY]: { icon: cilCheckCircle, color: 'text-success' },
    [STATUS.DEGRADED]: { icon: cilWarning, color: 'text-warning' },
    [STATUS.UNHEALTHY]: { icon: cilXCircle, color: 'text-danger' },
    [STATUS.UNKNOWN]: { icon: cilWarning, color: 'text-secondary' },
    [STATUS.CHECKING]: { icon: cilReload, color: 'text-info' },
    [STATUS.PENDING]: { icon: cilReload, color: 'text-secondary' }
  };

  const { icon, color } = iconMap[status] || iconMap[STATUS.UNKNOWN];
  
  if (status === STATUS.CHECKING) {
    return <CSpinner size="sm" color="info" />;
  }
  
  return <CIcon icon={icon} className={color} size="lg" />;
};

// Main component
const HealthCheck = () => {
  const [results, setResults] = useState({});
  const [isChecking, setIsChecking] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    const auth = getAuth();
    const user = auth.currentUser;
    setIsAuthenticated(!!user);
    setUserEmail(user?.email || null);
  }, []);

  // Check a single service
  const checkService = useCallback(async (service) => {
    setResults(prev => ({
      ...prev,
      [service.id]: { status: STATUS.CHECKING, latency: null, message: 'Checking...' }
    }));

    let result;

    switch (service.checkType) {
      case 'firebase':
        result = await checkFirebase();
        break;
      case 'websocket':
        result = await checkWebSocket(service.wsUrl, service.timeout);
        break;
      case 'smartthings':
        result = await checkSmartThings();
        break;
      default:
        // Get auth token for services that require authentication
        let authToken = null;
        if (service.requiresAuth) {
          authToken = await getAuthToken();
        }
        result = await checkHttpEndpoint(service, authToken);
    }

    setResults(prev => ({
      ...prev,
      [service.id]: result
    }));

    return result;
  }, []);

  // Check all services
  const checkAllServices = useCallback(async () => {
    setIsChecking(true);
    
    // Initialize all as pending
    const initialResults = {};
    SERVICES.forEach(service => {
      initialResults[service.id] = { status: STATUS.PENDING, latency: null, message: 'Waiting...' };
    });
    setResults(initialResults);

    // Check all services in parallel
    await Promise.all(SERVICES.map(service => checkService(service)));
    
    setLastChecked(new Date());
    setIsChecking(false);
  }, [checkService]);

  // Initial check on mount
  useEffect(() => {
    checkAllServices();
  }, [checkAllServices]);

  // Calculate overall health
  const getOverallHealth = () => {
    const statuses = Object.values(results);
    if (statuses.length === 0) return STATUS.PENDING;
    
    const healthyCount = statuses.filter(r => r.status === STATUS.HEALTHY).length;
    const unhealthyCount = statuses.filter(r => r.status === STATUS.UNHEALTHY).length;
    
    if (unhealthyCount > 0) return STATUS.DEGRADED;
    if (healthyCount === statuses.length) return STATUS.HEALTHY;
    return STATUS.UNKNOWN;
  };

  // Calculate health percentage
  const getHealthPercentage = () => {
    const statuses = Object.values(results);
    if (statuses.length === 0) return 0;
    
    const weights = {
      [STATUS.HEALTHY]: 100,
      [STATUS.DEGRADED]: 50,
      [STATUS.UNHEALTHY]: 0,
      [STATUS.UNKNOWN]: 25,
      [STATUS.PENDING]: 0,
      [STATUS.CHECKING]: 0
    };

    const total = statuses.reduce((sum, r) => sum + (weights[r.status] || 0), 0);
    return Math.round(total / statuses.length);
  };

  // Group services by category
  const groupedServices = SERVICES.reduce((acc, service) => {
    const category = service.category || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(service);
    return acc;
  }, {});

  const categoryNames = {
    core: 'Core Services',
    auth: 'Authentication',
    payments: 'Payments',
    blockchain: 'Blockchain & Storage',
    integrations: 'Integrations'
  };

  const overallHealth = getOverallHealth();
  const healthPercentage = getHealthPercentage();

  return (
    <CContainer fluid className="py-4">
      {/* Header */}
      <CRow className="mb-4">
        <CCol>
          <h2 className="mb-0">
            <CIcon icon={cilCheckCircle} className="me-2" />
            System Health Check
          </h2>
          <small className="mb-2">
            Environment: <strong>{config.environment}</strong>
            {lastChecked && (
              <> | Last checked: <strong>{lastChecked.toLocaleTimeString()}</strong></>
            )}
          </small>
        </CCol>
        <CCol xs="auto">
          <CButton 
            color="primary" 
            onClick={checkAllServices} 
            disabled={isChecking}
          >
            {isChecking ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Checking...
              </>
            ) : (
              <>
                <CIcon icon={cilReload} className="me-2" />
                Refresh All
              </>
            )}
          </CButton>
        </CCol>
      </CRow>

      {/* Overall Status Card */}
      <CRow className="mb-4">
        <CCol>
          <CCard>
            <CCardBody>
              <CRow className="align-items-center">
                <CCol xs="auto">
                  <StatusIcon status={overallHealth} />
                </CCol>
                <CCol>
                  <h4 className="mb-1">
                    Overall Status: <StatusBadge status={overallHealth} />
                  </h4>
                  <CProgress 
                    value={healthPercentage} 
                    color={healthPercentage > 80 ? 'success' : healthPercentage > 50 ? 'warning' : 'danger'}
                    className="mt-2"
                  />
                  <small className="mb-2">{healthPercentage}% of services healthy</small>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      {/* Service Status by Category */}
      {Object.entries(groupedServices).map(([category, services]) => (
        <CRow className="mb-4" key={category}>
          <CCol>
            <CCard>
              <CCardHeader>
                <strong>{categoryNames[category] || category}</strong>
              </CCardHeader>
              <CCardBody className="p-0">
                <CTable hover responsive className="mb-0">
                  <CTableHead>
                    <CTableRow>
                      <CTableHeaderCell style={{ width: '50px' }}>Status</CTableHeaderCell>
                      <CTableHeaderCell>Service</CTableHeaderCell>
                      <CTableHeaderCell>Description</CTableHeaderCell>
                      <CTableHeaderCell>Latency</CTableHeaderCell>
                      <CTableHeaderCell>Message</CTableHeaderCell>
                      <CTableHeaderCell style={{ width: '100px' }}>Action</CTableHeaderCell>
                    </CTableRow>
                  </CTableHead>
                  <CTableBody>
                    {services.map(service => {
                      const result = results[service.id] || { status: STATUS.PENDING };
                      return (
                        <CTableRow key={service.id}>
                          <CTableDataCell className="text-center">
                            <StatusIcon status={result.status} />
                          </CTableDataCell>
                          <CTableDataCell>
                            <CIcon icon={service.icon} className="me-2 mb-2" />
                            <strong>{service.name}</strong>
                          </CTableDataCell>
                          <CTableDataCell className="mb-2">
                            {service.description}
                          </CTableDataCell>
                          <CTableDataCell>
                            {result.latency !== null ? `${result.latency}ms` : '-'}
                          </CTableDataCell>
                          <CTableDataCell>
                            <small>{result.message || '-'}</small>
                          </CTableDataCell>
                          <CTableDataCell>
                            <CButton 
                              size="sm" 
                              color="light"
                              onClick={() => checkService(service)}
                              disabled={result.status === STATUS.CHECKING}
                            >
                              <CIcon icon={cilReload} size="sm" />
                            </CButton>
                          </CTableDataCell>
                        </CTableRow>
                      );
                    })}
                  </CTableBody>
                </CTable>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      ))}

      {/* Environment Info */}
      <CRow>
        <CCol>
          <CCard>
            <CCardHeader>
              <strong>Environment Configuration</strong>
            </CCardHeader>
            <CCardBody>
              {!isAuthenticated && (
                <CAlert color="warning" className="mb-3">
                  <strong>Not authenticated:</strong> Some services (like FHIR) require authentication.
                  Please log in to get accurate health status for all services.
                </CAlert>
              )}
              <CAlert color="info" className="mb-3">
                This page shows the connectivity status of all services. Some services may show 
                &quot;Unknown&quot; status due to CORS restrictions - this doesn&apos;t necessarily mean 
                the service is down.
              </CAlert>
              <CTable small bordered>
                <CTableBody>
                  <CTableRow>
                    <CTableDataCell><strong>Environment</strong></CTableDataCell>
                    <CTableDataCell>{config.environment}</CTableDataCell>
                  </CTableRow>
                  <CTableRow>
                    <CTableDataCell><strong>Authentication</strong></CTableDataCell>
                    <CTableDataCell>
                      {isAuthenticated ? (
                        <CBadge color="success">Authenticated as {userEmail}</CBadge>
                      ) : (
                        <CBadge color="warning">Not authenticated</CBadge>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                  <CTableRow>
                    <CTableDataCell><strong>FHIR URL</strong></CTableDataCell>
                    <CTableDataCell><code>{config.fhirUrl}</code></CTableDataCell>
                  </CTableRow>
                  <CTableRow>
                    <CTableDataCell><strong>API URL</strong></CTableDataCell>
                    <CTableDataCell><code>{config.apiUrl}</code></CTableDataCell>
                  </CTableRow>
                  <CTableRow>
                    <CTableDataCell><strong>Payment URL</strong></CTableDataCell>
                    <CTableDataCell><code>{config.paymentUrl}</code></CTableDataCell>
                  </CTableRow>
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </CContainer>
  );
};

export default HealthCheck;


